/**
 * CurrentTeamFieldView — the Current Team autocomplete field.
 *
 * Pure presentation: the text input plus, once the search has two characters
 * and there is something to show, the suggestion list.
 * @module components/organisms/ManuallyAddPlayerModal/CurrentTeamField/CurrentTeamFieldView
 */

import React, { memo } from "react";

import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { IOptionSelect } from "@/types/customSelectTypes";

/** Props for {@link CurrentTeamFieldView}. */
export interface CurrentTeamFieldViewProps {
  /** Whether dark mode is active. */
  readonly isDark: boolean;
  /** The field's current text. */
  readonly value: string;
  /** Whether the suggestion list should render. */
  readonly showSuggestions: boolean;
  /** The suggestions to offer. */
  readonly teamSuggestions: readonly IOptionSelect[];
  /** One press handler per suggestion, keyed by its value. */
  readonly suggestionHandlers: Readonly<Record<string, () => void>>;
  /** Text change handler, bound by the Container. */
  readonly onChangeText: (text: string) => void;
  /** Blur handler from react-hook-form. */
  readonly onBlur: () => void;
}

/**
 * Renders the Current Team field.
 * @param root0 - Field props
 * @param root0.isDark - Whether dark mode is active
 * @param root0.value - The field's current text
 * @param root0.showSuggestions - Whether the suggestion list renders
 * @param root0.teamSuggestions - The suggestions to offer
 * @param root0.suggestionHandlers - One press handler per suggestion
 * @param root0.onChangeText - Text change handler
 * @param root0.onBlur - Blur handler
 * @returns The field.
 */
const CurrentTeamFieldView = ({
  isDark,
  value,
  showSuggestions,
  teamSuggestions,
  suggestionHandlers,
  onChangeText,
  onBlur,
}: CurrentTeamFieldViewProps) => (
  <Stack space="1">
    <Input
      variant="outline"
      className="border"
      size="xl"
      field={{
        placeholder: "Enter team name",
        type: "text",
        value: value ?? "",
        keyboardType: "default",
        inputMode: "text",
        autoCorrect: false,
        autoCapitalize: "words",
        onChangeText,
        onBlur,
        className: "font-body",
        testID: "manual-player-team",
      }}
    />
    {showSuggestions && (
      <ScrollView
        className={`max-h-40 rounded-sm border border-outline-subtle ${
          isDark ? "bg-surface-action" : "bg-white"
        }`}
        nestedScrollEnabled
      >
        {teamSuggestions.map(team => (
          <Pressable
            key={team.value}
            onPress={suggestionHandlers[team.value]}
            className="px-3 py-2 active:opacity-80"
            testID={`team-suggestion-${team.value}`}
          >
            <Text variant="body" className="text-content-primary">
              {team.label}
            </Text>
          </Pressable>
        ))}
      </ScrollView>
    )}
  </Stack>
);

CurrentTeamFieldView.displayName = "CurrentTeamFieldView";

export default memo(CurrentTeamFieldView);
